import type { CSSProperties, ReactNode } from 'react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import type { NavigationItem } from '@/shared/types/navigation.types';

/**
 * - `--sidebar-width: 75px` overrides the primitive's default (16rem) to
 *   match the rail width the design specifies.
 * - The wrapper's intrinsic `min-h-svh w-full` flex layout (intended for
 *   app-root usage) is neutralised with inline styles so the provider can
 *   live inside the `<aside class="shrink-0">` slot owned by the page layout
 *   without expanding to the viewport.
 */
const ICON_RAIL_WIDTH_PX = 75;

export const ICON_RAIL_PROVIDER_STYLE: CSSProperties = {
  '--sidebar-width': `${String(ICON_RAIL_WIDTH_PX)}px`,
  width: 'auto',
  minHeight: 0,
  height: '100%',
} as CSSProperties;

/**
 * Wraps a 40px control so it occupies a full rail row, matching the nav items.
 *
 * The padding lives here rather than on the control so the focus ring keeps hugging
 * the control itself.
 */
export function RailRow({ children }: { children: ReactNode }) {
  return (
    <div data-testid="rail-row" className="py-xs">
      {children}
    </div>
  );
}

/**
 * Renders a single rail nav item using the primitive's
 * `orientation="vertical"` mode: icon ABOVE label, or the icon alone with the
 * label in a tooltip when `iconOnly`.
 *
 * The icon holder's `size-3xl` must match the primitive's active-indicator
 * geometry: the indicator is an absolutely positioned square sized in the
 * design system, not here, so a mismatch shows as a pill that misses the icon.
 *
 * Falls back to the baked-in icon JSX (`item.icon`) when `IconComponent` is
 * missing, which keeps the function safe if the icon set ever drifts.
 */
export function VerticalRailItem({
  item,
  iconOnly = false,
}: {
  item: NavigationItem;
  iconOnly?: boolean;
}) {
  const { IconComponent, icon, active, label, accessibleLabel, onClick } = item;
  const name = accessibleLabel ?? label;
  // Tessra truncates a vertical item's label to one line; a 75px rail needs two for "Connected apps".
  const labelClassName = `whitespace-normal! text-center ${active ? 'text-primary font-medium' : 'text-foreground'}`;
  const iconClassName = active ? 'size-md text-primary' : 'size-md text-foreground-subtle';

  const button = (
    <SidebarMenuButton orientation="vertical" isActive={active} onClick={onClick} aria-label={name}>
      <span className="flex size-3xl items-center justify-center">
        {IconComponent ? <IconComponent className={iconClassName} /> : icon}
      </span>
      {!iconOnly && <span className={labelClassName}>{label}</span>}
    </SidebarMenuButton>
  );

  if (!iconOnly) return button;

  // The primitive's own `tooltip` prop only shows while its sidebar is collapsed, which a rail
  // pinned open never is, so the name the label no longer prints is supplied here.
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right">{name}</TooltipContent>
    </Tooltip>
  );
}

export interface IconRailProps {
  /** The top row, for example the workspace or organization switcher. */
  header: ReactNode;
  items: readonly NavigationItem[];
  /** Pinned above the footer, apart from the main destinations. */
  bottomItems?: readonly NavigationItem[];
  /** The last row, for example the user menu. */
  footer?: ReactNode;
  /** Names the rail as a landmark; the primitive is a plain div. */
  label?: string;
  /** Drops the visible labels, naming each destination in a tooltip instead. */
  iconOnly?: boolean;
  testId?: string;
}

/**
 * The 75px rail the app navigates with: a header row, destinations as icon over label, and a
 * footer. Shared so every rail in the app reads the same, whatever it navigates.
 */
export function IconRail({
  header,
  items,
  bottomItems = [],
  footer,
  label,
  iconOnly = false,
  testId,
}: IconRailProps) {
  return (
    <TooltipProvider>
      <SidebarProvider defaultOpen style={ICON_RAIL_PROVIDER_STYLE}>
        <Sidebar
          collapsible="none"
          className="border-r border-muted"
          {...(label ? { role: 'navigation', 'aria-label': label } : {})}
          {...(testId ? { 'data-testid': testId } : {})}
        >
          <SidebarHeader className="items-center justify-center p-xs">{header}</SidebarHeader>

          {/* `no-scrollbar` keeps the rail scrollable on short viewports without
            painting a bar into the 75px column, where it would sit on top of the
            nav labels rather than beside them. */}
          <SidebarContent className="no-scrollbar items-center py-lg">
            <SidebarGroup className="p-0">
              <SidebarGroupContent>
                <SidebarMenu className="gap-md">
                  {items.map((item) => (
                    <SidebarMenuItem key={item.id}>
                      <VerticalRailItem item={item} iconOnly={iconOnly} />
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="items-center gap-md py-md px-0">
            {bottomItems.length > 0 && (
              <SidebarMenu className="gap-md">
                {bottomItems.map((item) => (
                  <SidebarMenuItem key={item.id}>
                    <VerticalRailItem item={item} iconOnly={iconOnly} />
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            )}
            {footer}
          </SidebarFooter>
        </Sidebar>
      </SidebarProvider>
    </TooltipProvider>
  );
}
